import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { IconType } from '@factorialco/f0-react'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Add, Delete } from '@factorialco/f0-react/icons/app'
import { PaymentsDataCollectionDocument } from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { ConfirmRemovePaymentRequestModal } from 'modules/banking/components/ConfirmRemovePaymentRequestModal'

import { mapFiltersToVariables } from './filters'
import type { Payment, PaymentBase } from './types'
import { isPaymentBase, isMappedPaymentRequestChild } from './types'
import { usePaymentItemActions } from './usePaymentItemActions'
import type { MappedPaymentRequestChild } from './usePaymentNestedPaymentRequests'
import { usePaymentNestedPaymentRequests } from './usePaymentNestedPaymentRequests'
import { usePaymentsDataCollection } from './usePaymentsDataCollection'
import { usePaymentsFilters } from './usePaymentsFilters'

interface Props {
  companyId: number
  locale: string
  canSeeSepaTransferAction: boolean
  onNewPaymentAction: () => void
}

type ItemAction =
  | {
      id: string
      label: string
      icon?: IconType
      onClick: () => void
      type?: 'primary' | 'secondary' | 'other'
      critical?: boolean
    }
  | {
      type: 'separator'
    }

interface RemoveConfirmationData {
  paymentRequestLabel: string
  transfersCount: number
  parentPaymentId: number
  transferIds: number[]
  refetch: () => void
}

export const PaymentsDataCollection = ({
  companyId: _companyId,
  locale,
  canSeeSepaTransferAction,
  onNewPaymentAction,
}: Props) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const [removeConfirmation, setRemoveConfirmation] = useState<RemoveConfirmationData | null>(null)

  const filterDefinitions = usePaymentsFilters()
  const { sortings, defaultSortings, columns } = usePaymentsDataCollection({
    locale,
  })
  const { getItemActions: getPaymentItemActions } = usePaymentItemActions({
    canSeeSepaTransferAction,
  })
  const {
    itemsWithChildren,
    childrenCount,
    fetchChildren,
    removePaymentRequestFromPayment,
    isRemoving,
  } = usePaymentNestedPaymentRequests()

  // Get actions for PaymentRequest children
  const getPaymentRequestItemActions = useCallback(
    (item: MappedPaymentRequestChild, refetch: () => void): ItemAction[] => {
      const actions: ItemAction[] = []

      // Only show remove action if the PaymentRequest status is 'init'
      if (item.status === 'init') {
        actions.push({
          id: 'remove_from_bulk_payment',
          label: i18n.t('banking.bulkPayments.detail.removePaymentRequest'),
          icon: Delete,
          critical: true,
          onClick: () => {
            // Show confirmation modal instead of directly removing
            setRemoveConfirmation({
              paymentRequestLabel: item.label,
              transfersCount: item._paymentRequestData.itemsCount,
              parentPaymentId: item._parentPaymentId,
              transferIds: item._paymentRequestData.transferIds,
              refetch,
            })
          },
        })
      }

      return actions
    },
    []
  )

  // Combined item actions for both payments and payment requests
  const getItemActions = useCallback(
    (item: PaymentBase, refetch?: () => void) => {
      // Check if it's a PaymentRequest child
      const paymentItem: Payment = item
      if (isMappedPaymentRequestChild(paymentItem)) {
        return getPaymentRequestItemActions(paymentItem, refetch ?? (() => {}))
      }
      // It's a payment
      if (!isPaymentBase(item)) {
        return []
      }
      return getPaymentItemActions(item)
    },
    [getPaymentItemActions, getPaymentRequestItemActions]
  )

  const onRowClick = useCallback(
    (item: PaymentBase): (() => void) => {
      return () => {
        const paymentItem: Payment = item
        if (isMappedPaymentRequestChild(paymentItem)) {
          // It's a PaymentRequest - navigate to PaymentRequest detail page
          navigate(
            navigation.finance.treasury.banking.payments.paymentRequests.paymentRequestId.fullPath({
              paymentRequestId: String(paymentItem._paymentRequestData.paymentRequestId),
            })
          )
        } else {
          // It's a payment - navigate to payment detail page
          navigate(
            navigation.finance.banking.payments.paymentId.fullPath({
              id: String(item.id),
            })
          )
        }
      }
    },
    [navigate, navigation]
  )

  const baseSource = useGraphqlDataCollectionSource({
    document: PaymentsDataCollectionDocument,
    mapData: (data) => data.banking.paymentsConnection.edges.map((edge) => edge.node),
    mapVariables: ({ search, sortings: mapSortings, filters }) => {
      const mappedFilters = mapFiltersToVariables(filters)

      return {
        sortOrder: mapSortings?.[0],
        search,
        ...mappedFilters,
      }
    },
    paginationType: 'pages',
    perPage: 10,
    mapPagination: (data) => ({
      total: data.banking.paymentsConnection.totalCount ?? 0,
      hasNextPage: data.banking.paymentsConnection.pageInfo.hasNextPage ?? false,
    }),
    search: { enabled: true },
    filters: filterDefinitions,
    sortings,
    defaultSortings,
    itemActions: getItemActions,
    itemOnClick: onRowClick,
    primaryActions: () => [
      {
        label: i18n.t('banking.payments.actions.create_bulk_payment'),
        icon: Add,
        onClick: () => {
          track(trackingEvents.accounting.payments.clickCreateBulkPayment)
          onNewPaymentAction()
        },
      },
    ],
  })

  // Extend source with nested table functionality
  const source = useMemo(() => {
    return {
      ...baseSource,
      itemsWithChildren,
      childrenCount,
      fetchChildren,
      // Override itemOnClick to handle both payments and payment requests
      itemOnClick: (item: PaymentBase | MappedPaymentRequestChild) => {
        const paymentItem: Payment = item
        if (isMappedPaymentRequestChild(paymentItem)) {
          // It's a PaymentRequest - navigate to detail page
          return () =>
            navigate(
              navigation.finance.treasury.banking.payments.paymentRequests.paymentRequestId.fullPath({
                paymentRequestId: String(paymentItem._paymentRequestData.paymentRequestId),
              })
            )
        }
        // For payments, use the original handler
        const paymentBaseItem: PaymentBase = item
        return onRowClick(paymentBaseItem)
      },
    }
  }, [baseSource, itemsWithChildren, childrenCount, fetchChildren, onRowClick, navigate, navigation])

  const handleConfirmRemove = async () => {
    if (!removeConfirmation) return

    await removePaymentRequestFromPayment(
      removeConfirmation.parentPaymentId,
      removeConfirmation.transferIds,
      removeConfirmation.refetch
    )

    setRemoveConfirmation(null)
  }

  const handleCloseRemoveModal = () => {
    if (!isRemoving) {
      setRemoveConfirmation(null)
    }
  }

  return (
    <>
      <OneDataCollection
        id="payments/v1"
        storage={{ features: ['filters', 'sortings', 'search'] }}
        fullHeight
        emptyStates={{
          'no-data': {
            title: i18n.t('banking.payments.data_table.empty_state.title'),
            description: i18n.t('banking.payments.data_table.empty_state.description_2'),
          },
        }}
        source={source}
        visualizations={[
          {
            type: 'table',
            options: {
              allowColumnHiding: true,
              allowColumnReordering: true,
              columns,
            },
          },
        ]}
      />

      {removeConfirmation && (
        <ConfirmRemovePaymentRequestModal
          paymentRequestLabel={removeConfirmation.paymentRequestLabel}
          transfersCount={removeConfirmation.transfersCount}
          isLoading={isRemoving}
          onConfirm={handleConfirmRemove}
          onClose={handleCloseRemoveModal}
        />
      )}
    </>
  )
}
